/**
 * 章节回退处置页 (B3-min)
 *
 * Phase 3-3 / Plan §5.6 B3-min / Design §8.2 content 权限点。
 *
 * 权限：
 *   - content:read  — 查看章节列表 + 详情（super_admin + operator + support）
 *   - content:rollback — 回退操作（super_admin + support）
 *
 * 功能：
 *   - 查询表单：输入 workId → 查找章节列表；输入 chapterId → 直接打开详情
 *   - 章节列表 Table：position / title / body_version / lock_status / 最后修改时间 / [详情]
 *   - 详情抽屉（width=720）：
 *       - 当前正文（Typography.Paragraph ellipsis 展开）
 *       - 元数据 Descriptions：body_version / lock_status / created_at / updated_at
 *       - 关联 ai_task：task_id + prompt_version_id
 *       - snapshots 历史：Timeline 倒序展示
 *       - 回退按钮（底部固定区，usePermission(CONTENT_ROLLBACK) 控制 disabled）
 *   - 回退 Modal：reason TextArea(5..200)字数计数 + notifyAuthor Checkbox(默认勾) + 提交
 *   - 回退成功：message.success + 关闭 Modal + 刷新详情
 *
 * testid:
 *   chapter-rollback-page / chapter-rollback-search-btn / chapter-rollback-detail-drawer /
 *   chapter-rollback-rollback-btn / chapter-rollback-confirm-modal
 */
import { useState } from 'react';
import {
  Input,
  Button,
  Table,
  Tag,
  Space,
  Drawer,
  Modal,
  Form,
  Checkbox,
  message,
  Result,
  Typography,
  Descriptions,
  Timeline,
  Tooltip,
  Row,
  Col,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listChaptersByWorkId,
  getChapterDetail,
  rollbackChapter,
  type ChapterDto,
  type ChapterFullDetailDto,
} from '../../api/chapter-rollback';
import { PermissionGate } from '../../components/PermissionGate';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../contracts/permissions';

const { TextArea } = Input;
const { Paragraph, Text } = Typography;

// ── 辅助函数 ─────────────────────────────────────────────────────────────────

/** epoch ms → 本地时间字符串 */
function formatEpoch(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  return new Date(v).toLocaleString('zh-CN');
}

/** lock_status → Tag 颜色 */
function lockStatusTag(status: string) {
  switch (status) {
    case 'locked': return <Tag color="red">locked</Tag>;
    case 'growing': return <Tag color="blue">growing</Tag>;
    case 'confirmed': return <Tag color="green">confirmed</Tag>;
    default: return <Tag>{status}</Tag>;
  }
}

// ── 主页面（权限门控）────────────────────────────────────────────────────────

export default function ChapterRollbackPage() {
  return (
    <PermissionGate
      point={PERMISSIONS.CONTENT_READ}
      fallback={
        <Result status="403" title="403" subTitle="无权访问章节回退处置" />
      }
    >
      <ChapterRollbackPageInner />
    </PermissionGate>
  );
}

function ChapterRollbackPageInner() {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  // 权限
  const canRollback = usePermission(PERMISSIONS.CONTENT_ROLLBACK);

  // 查询表单状态
  const [workId, setWorkId] = useState('');
  const [chapterId, setChapterId] = useState('');
  const [activeWorkId, setActiveWorkId] = useState<string | undefined>(undefined);

  // 分页
  const [page, setPage] = useState(1);
  const pageSize = 20;

  // 详情抽屉
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeChapterId, setActiveChapterId] = useState<string | null>(null);

  // 回退 Modal
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [reasonLength, setReasonLength] = useState(0);

  // ── 查询 ─────────────────────────────────────────────────────────────────

  // 章节列表查询
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: ['chapter-list', activeWorkId, page, pageSize],
    queryFn: () => listChaptersByWorkId(activeWorkId!, pageSize, (page - 1) * pageSize),
    enabled: !!activeWorkId,
  });

  // 章节详情查询（抽屉打开时）
  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['chapter-detail', activeChapterId],
    queryFn: () => getChapterDetail(activeChapterId!),
    enabled: !!activeChapterId && drawerOpen,
  });

  // ── 操作函数 ─────────────────────────────────────────────────────────────

  function handleSearch() {
    if (chapterId.trim()) {
      // chapterId 单查 → 直接打开详情抽屉
      setActiveChapterId(chapterId.trim());
      setDrawerOpen(true);
      return;
    }
    if (workId.trim()) {
      setActiveWorkId(workId.trim());
      setPage(1);
      return;
    }
    message.warning('请输入 workId 或 chapterId');
  }

  function handleOpenDetail(id: string) {
    setActiveChapterId(id);
    setDrawerOpen(true);
  }

  function handleCloseDrawer() {
    setDrawerOpen(false);
    setRollbackModalOpen(false);
  }

  async function handleRollbackSubmit(values: { reason: string; notifyAuthor: boolean }) {
    if (!activeChapterId) return;
    setRollbackLoading(true);
    try {
      await rollbackChapter(activeChapterId, values.reason, values.notifyAuthor);
      // P2-2 review fix：措辞改为"已尝试通知作者"，更准确反映 content-pipeline best-effort
      // silent-fail 语义（design §7.3：admin-service 不保证通知一定送达）
      const notifyMsg = values.notifyAuthor ? '，已尝试通知作者' : '';
      message.success(`已回退${notifyMsg}`);
      setRollbackModalOpen(false);
      form.resetFields();
      setReasonLength(0);
      // 刷新详情（snapshots 列表新增一条）
      queryClient.invalidateQueries({ queryKey: ['chapter-detail', activeChapterId] });
      // 刷新列表（body_version 已更新）
      if (activeWorkId) {
        queryClient.invalidateQueries({ queryKey: ['chapter-list', activeWorkId] });
      }
    } catch {
      message.error('回退失败，请检查是否存在可回滚快照或检查 reason 长度（5..200 字符）');
    } finally {
      setRollbackLoading(false);
    }
  }

  // ── 表格列 ────────────────────────────────────────────────────────────────

  const columns: ColumnsType<ChapterDto> = [
    {
      title: '顺序',
      dataIndex: 'sortOrder',
      key: 'sortOrder',
      width: 60,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '版本号',
      dataIndex: 'bodyVersion',
      key: 'bodyVersion',
      width: 80,
    },
    {
      title: '锁定状态',
      dataIndex: 'lockStatus',
      key: 'lockStatus',
      width: 100,
      render: lockStatusTag,
    },
    {
      title: '最后修改',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 160,
      render: formatEpoch,
    },
    {
      title: '操作',
      key: 'actions',
      width: 80,
      render: (_: unknown, row: ChapterDto) => (
        <Button
          size="small"
          onClick={() => handleOpenDetail(row.chapterId)}
        >
          详情
        </Button>
      ),
    },
  ];

  // ── 详情抽屉内容 ──────────────────────────────────────────────────────────

  function renderDrawerContent(detail: ChapterFullDetailDto) {
    const { chapter, snapshots, recentAiTask } = detail;
    return (
      <>
        <Descriptions
          bordered
          column={2}
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Descriptions.Item label="work_id" span={2}>{chapter.workId}</Descriptions.Item>
          <Descriptions.Item label="chapter_id" span={2}>{chapter.chapterId}</Descriptions.Item>
          <Descriptions.Item label="标题" span={2}>{chapter.title}</Descriptions.Item>
          <Descriptions.Item label="顺序">{chapter.sortOrder}</Descriptions.Item>
          <Descriptions.Item label="版本号">{chapter.bodyVersion}</Descriptions.Item>
          <Descriptions.Item label="锁定状态">{lockStatusTag(chapter.lockStatus)}</Descriptions.Item>
          <Descriptions.Item label="创建时间">{formatEpoch(chapter.createdAt)}</Descriptions.Item>
          <Descriptions.Item label="更新时间">{formatEpoch(chapter.updatedAt)}</Descriptions.Item>
        </Descriptions>

        {/* 正文（支持 ellipsis 展开全文） */}
        <div style={{ marginBottom: 16 }}>
          <Text strong>当前正文</Text>
          <Paragraph
            ellipsis={{ rows: 5, expandable: true, symbol: '展开全文' }}
            style={{ marginTop: 8, whiteSpace: 'pre-wrap' }}
          >
            {chapter.body ?? '（无正文）'}
          </Paragraph>
        </div>

        {/* 关联 AI 任务 */}
        {recentAiTask && (
          <div style={{ marginBottom: 16 }}>
            <Text strong>关联 AI 任务</Text>
            <Descriptions bordered column={2} size="small" style={{ marginTop: 8 }}>
              <Descriptions.Item label="task_id">{recentAiTask.taskId}</Descriptions.Item>
              <Descriptions.Item label="状态">{recentAiTask.status}</Descriptions.Item>
              <Descriptions.Item label="prompt 版本" span={2}>
                {recentAiTask.promptVersionId ?? '（无）'}
              </Descriptions.Item>
            </Descriptions>
          </div>
        )}

        {/* Snapshots 历史 */}
        <div style={{ marginBottom: 16 }}>
          <Text strong>版本历史（{snapshots.length} 条）</Text>
          {snapshots.length === 0 ? (
            <div style={{ marginTop: 8, color: '#888' }}>无历史快照，不可回退</div>
          ) : (
            <Timeline
              style={{ marginTop: 12 }}
              items={snapshots.map((s) => ({
                key: s.snapshotId,
                children: (
                  <div>
                    <Space>
                      <Text strong>v{s.bodyVersionBefore ?? '?'}</Text>
                      {s.canRollback ? <Tag color="green">可回退</Tag> : <Tag>已失效</Tag>}
                      <Text type="secondary">{formatEpoch(s.createdAt)}</Text>
                    </Space>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#555' }}>
                      {s.contentBefore}
                    </div>
                  </div>
                ),
              }))}
            />
          )}
        </div>
      </>
    );
  }

  // ── 渲染 ─────────────────────────────────────────────────────────────────

  return (
    <div data-testid="chapter-rollback-page" style={{ padding: 24 }}>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>章节回退处置 (B3-min)</Typography.Title>

      {/* 查询表单 */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col flex="240px">
          <Input
            placeholder="输入 workId 查章节列表"
            value={workId}
            onChange={(e) => setWorkId(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
          />
        </Col>
        <Col flex="240px">
          <Input
            placeholder="或输入 chapterId 直接查详情"
            value={chapterId}
            onChange={(e) => setChapterId(e.target.value)}
            onPressEnter={handleSearch}
            allowClear
          />
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={handleSearch}
            data-testid="chapter-rollback-search-btn"
          >
            查找章节
          </Button>
        </Col>
      </Row>

      {/* 章节列表 */}
      {activeWorkId && (
        <Table<ChapterDto>
          rowKey="chapterId"
          columns={columns}
          dataSource={listData?.items ?? []}
          loading={listLoading}
          pagination={{
            current: page,
            pageSize,
            total: listData?.total ?? 0,
            onChange: (p) => setPage(p),
          }}
        />
      )}

      {/* 章节详情抽屉 */}
      <Drawer
        title={activeChapterId ? `章节详情: ${activeChapterId}` : '章节详情'}
        open={drawerOpen}
        onClose={handleCloseDrawer}
        width={720}
        data-testid="chapter-rollback-detail-drawer"
        footer={
          <div style={{ textAlign: 'right' }}>
            {/* P2-5 review fix：snapshots 空时 disabled + Tooltip 提示 */}
            <Tooltip title={
              !canRollback
                ? '无回退权限'
                : (detailData?.snapshots.length === 0 ? '无可回退版本' : undefined)
            }>
              <Button
                type="primary"
                danger
                disabled={!canRollback || !detailData || detailData.snapshots.length === 0}
                onClick={() => setRollbackModalOpen(true)}
                data-testid="chapter-rollback-rollback-btn"
              >
                回退到上一版
              </Button>
            </Tooltip>
          </div>
        }
      >
        {detailLoading && <div>加载中...</div>}
        {!detailLoading && detailData && renderDrawerContent(detailData)}
        {!detailLoading && !detailData && activeChapterId && (
          <Result status="404" title="章节不存在" />
        )}
      </Drawer>

      {/* 回退 Modal */}
      <Modal
        title="确认回退章节"
        open={rollbackModalOpen}
        onCancel={() => {
          setRollbackModalOpen(false);
          form.resetFields();
          setReasonLength(0);
        }}
        footer={null}
        data-testid="chapter-rollback-confirm-modal"
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ notifyAuthor: true }}
          onFinish={handleRollbackSubmit}
        >
          <Form.Item
            label={`回退原因（${reasonLength}/200 字，5..200 字符）`}
            name="reason"
            rules={[
              { required: true, message: '请输入回退原因' },
              { min: 5, message: '至少 5 个字符' },
              { max: 200, message: '不超过 200 个字符' },
            ]}
          >
            <TextArea
              rows={4}
              maxLength={200}
              showCount
              placeholder="请描述回退原因，例如：AI 生成内容错误，捏造了不实家史段落"
              onChange={(e) => setReasonLength(e.target.value.length)}
            />
          </Form.Item>

          <Form.Item name="notifyAuthor" valuePropName="checked">
            <Checkbox>通知作者（通知由系统 best-effort 发送，默认勾选）</Checkbox>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button
                onClick={() => {
                  setRollbackModalOpen(false);
                  form.resetFields();
                  setReasonLength(0);
                }}
              >
                取消
              </Button>
              <Button
                type="primary"
                danger
                htmlType="submit"
                loading={rollbackLoading}
                disabled={rollbackLoading}
              >
                确认回退
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
